import { yupResolver } from "@hookform/resolvers/yup";
import { get } from "lodash";
import { useCallback, useEffect, useMemo, type FC } from "react";
import { Controller, useForm, type Control, type FieldError, type UseFormSetValue } from "react-hook-form";

import { useRootDispatch } from "@ds/hooks/redux-hooks";

import { playersActions } from "@ds/modules/devices/players/redux/slice";
import { getCreateRoleValidationSchema } from "@ds/modules/devices/players/utils/validation-schema";
import { getRoleOptions, switchPlayerRole } from "@ds/modules/experiences/utils/player-roles";
import { FormationControl } from "@ds/modules/settings/projects/components/forms/controls/formation-control";
import { type FormationControlFormValues } from "@ds/modules/settings/projects/components/forms/controls/types";

import { Dropdown, type DropdownChangeEvent } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { WizardPage, type WizardChildStepProps } from "@ds/components/wizard";

import { getNewPlayerRoleFieldPath, NEW_PLAYER_ID, withNewPlayer } from "../../../utils/new-player-role";
import { useCreatePlayer } from "./create-player-context";

/** The new player's role in its experience and its values of the role's fields, which the player is added with */
export const StepRole: FC<
  WizardChildStepProps & { roles: FormationRoleMap; properties: TemplateFormationFieldPropertyMap }
> = ({ isActive, nextStep, previousStep, roles, properties, ...rest }) => {
  const [currentPlayerData, submitPlayerData] = useCreatePlayer();
  const { experience } = currentPlayerData;
  const resolver = useMemo(
    () => yupResolver(getCreateRoleValidationSchema(roles, properties, experience)),
    [roles, properties, experience],
  );

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    handleSubmit,
    reset,
    watch,
  } = useForm<PlayerFormData>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: currentPlayerData,
    resolver,
  });

  useEffect(() => {
    if (isActive) {
      reset(currentPlayerData);
    }
  }, [currentPlayerData, isActive, reset]);

  const formation = watch("formation");
  const role = formation?.role ? roles[formation.role] : undefined;
  const { devices, playerIds } = withNewPlayer(experience, formation);

  const onRoleChangeHandler = useCallback(
    (e: DropdownChangeEvent) =>
      setValue("formation", switchPlayerRole(roles, getValues("formation"), (e.value as string | null) ?? null), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }),
    [roles, getValues, setValue],
  );

  const dispatch = useRootDispatch();
  const onNextClickHandler = useCallback(() => {
    const dataToSubmit = getValues();
    submitPlayerData(dataToSubmit);
    dispatch(
      playersActions.createPlayer({
        player: dataToSubmit.player,
        pairingCode: dataToSubmit.pairingCode,
        // without a role picked, the player joins the experience without one, same as with no Role page
        formation: dataToSubmit.formation?.role ? dataToSubmit.formation : undefined,
      }),
    );
    if (nextStep) {
      nextStep();
    }
  }, [dispatch, submitPlayerData, getValues, nextStep]);

  const onBackClickHandler = useCallback(() => {
    submitPlayerData(getValues());
    if (previousStep) {
      previousStep();
    }
  }, [submitPlayerData, getValues, previousStep]);

  return (
    <WizardPage
      {...rest}
      title="Assign role"
      previousStep={onBackClickHandler}
      nextStep={handleSubmit(onNextClickHandler)}
    >
      <Controller
        name="formation.role"
        control={control}
        render={({ field: { ref, value }, fieldState }) => (
          <LabelWrapper
            label="Role"
            infoText="You can also set the role later in the experience."
            className="col-8"
            error={fieldState.error}
          >
            <Dropdown
              inputRef={ref}
              value={value}
              options={getRoleOptions(roles, devices, playerIds, NEW_PLAYER_ID)}
              optionLabel="label"
              optionValue="value"
              optionDisabled="disabled"
              placeholder="Pick a role"
              showClear
              ariaLabel="Role"
              onChange={onRoleChangeHandler}
            />
          </LabelWrapper>
        )}
      />
      {(role?.fields || [])
        .filter(field => !!properties[field])
        .map(field => (
          // no required mark: a missing value only leaves the experience incomplete, it doesn't block adding the player
          <LabelWrapper
            key={field}
            label={properties[field].title}
            className="col-8"
            error={get(errors, getNewPlayerRoleFieldPath(field)) as FieldError | undefined}
          >
            <FormationControl
              name={`formation.properties.${field}`}
              control={control as unknown as Control<FormationControlFormValues>}
              setValue={setValue as unknown as UseFormSetValue<FormationControlFormValues>}
              property={properties[field]}
              excludeLabel
            />
          </LabelWrapper>
        ))}
    </WizardPage>
  );
};
